<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { Select } from '@client/components/select';

  import { AccountFormContent } from '@client/pages/marketing/targeting/accounts/modals/shared';
  import { GroupFormContent } from '@client/pages/marketing/targeting/groups/modals/shared';

  import { CampaignsService } from '@client/services/campaigns';
  import { GroupsService } from '@client/services/groups';
  import { TagsService } from '@client/services/tags';
  import { TargetingService } from '@client/services/targeting';

  import { generatePastelColors } from '@common/utils/colors';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const targetingTypes = [
    { label: $format('label.GROUP'), value: 'group' },
    { label: $format('label.DOMAIN'), value: 'domain' },
    { label: $format('label.RECIPIENT'), value: 'recipient' }
  ];

  const groupTypes = [
    { label: $format('label.RECIPIENT'), value: 'recip' },
    { label: $format('label.SENDER'), value: 'sender' }
  ];

  let selectedType = targetingTypes[0];
  let accountData = { name: '', campaigns: [], tags: [] };
  let groupData = { target: 'recip', groups: [], campaigns: [], tags: [], status: false };
  let valid = true;

  let campaigns = [];
  let tags = [];
  let groups = [];
  let existingAccounts = [];

  const colors = generatePastelColors(1000);
  const colorAssignments = {};

  const createDatasetOptions = (value) => {
    if (!colorAssignments[value]) {
      colorAssignments[value] = colors.pop();
    }

    return {
      color: colorAssignments[value],
      label: value,
      value: value
    };
  };

  const getCampaigns = async () => {
    const response = await CampaignsService.getAll();
    campaigns = Array.isArray(response) ? response.map(({ rulename }) => createDatasetOptions(rulename)) : [];
  };

  const getGroups = async () => {
    const response = await GroupsService.getSenderGroups();
    groups = Array.isArray(response) ? response.map(({ groupname }) => createDatasetOptions(groupname)) : [];
  };

  const getTags = async () => {
    const response = await TagsService.getAll();
    tags = Array.isArray(response) ? response.map((text) => createDatasetOptions(text)) : [];
  };

  const getExistingTargeting = async () => {
    const targeting = await TargetingService.get();
    existingAccounts = Array.isArray(targeting?.accounts) ? targeting.accounts : [];
  };

  const onKeyUpHandler = (e) => {
    const exists = existingAccounts.some((account) => account.name === e.target.value);
    valid = !exists;
  };

  const executeTargetingCreate = async () => {
    const currentTargeting = await TargetingService.get();

    let payload;

    if (selectedType?.value === 'group') {
      const newDepartment = {
        targettype: typeof groupData.target === 'string' ? groupData.target : groupData.target.value,
        sendergroups: (groupData.groups || []).map(({ value }) => ({ groupname: value })),
        ads: (groupData.campaigns || []).map(({ value }) => ({ text: value })),
        tags: (groupData.tags || []).map(({ value }) => ({ text: value }))
      };

      if (groupData.status) {
        newDepartment.status = groupData.status;
      }

      payload = {
        accounts: currentTargeting.accounts || [],
        departments: [newDepartment, ...(currentTargeting.departments || []).map(({ _id, ...rest }) => rest)]
      };
    } else {
      const newAccount = {
        name: accountData.name,
        tags: (accountData.tags || []).map(({ value }) => ({ text: value })),
        ads: (accountData.campaigns || []).map(({ value }) => ({ text: value }))
      };

      payload = {
        accounts: [...(currentTargeting.accounts || []), newAccount],
        departments: currentTargeting.departments || []
      };
    }

    const response = await TargetingService.update(payload);

    if (response?.status >= 400) {
      throw new Error($format('label.FAILURE.CREATE_TARGETING'));
    }

    step.data = {
      id: selectedType?.value === 'group' ? 'Groups' : selectedType?.value === 'domain' ? 'Domains' : 'Recipients',
      rollbackData: {
        originalTargeting: currentTargeting
      }
    };

    return response;
  };

  const rollbackTargetingCreate = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    const { originalTargeting } = step.data.rollbackData;

    // Restore the original targeting
    const response = await TargetingService.update(originalTargeting);

    if (response?.status >= 400) {
      throw new Error($format('label.FAILURE.RESTORE_TARGETING'));
    }

    return { success: true };
  };

  onMount(async () => {
    await Promise.all([getCampaigns(), getGroups(), getTags(), getExistingTargeting()]);

    if (onExecute) {
      onExecute(executeTargetingCreate);
    }

    if (onRollback) {
      onRollback(rollbackTargetingCreate);
    }
  });

  $: {
    if (selectedType?.value === 'group') {
      nextDisabled = !groupData.groups?.length;
    } else {
      nextDisabled = !accountData.name || !valid;
    }
  }
</script>

<div class="targeting-create-form">
  <div class="section" aria-labelledby="target-label">
    <p id="target-label">{$format('label.TARGET')}</p>
    <Select
      items={targetingTypes}
      bind:value={selectedType}
      clearable={false}
      showChevron
      placeholder={$format('label.PLACEHOLDER.SELECT_TARGETING_TYPE')}
    />
  </div>

  {#if selectedType}
    {#if selectedType.value === 'group'}
      <GroupFormContent bind:route={groupData} types={groupTypes} {groups} {campaigns} {tags} />
    {:else}
      <AccountFormContent
        bind:data={accountData}
        isDomains={selectedType.value === 'domain'}
        bind:valid
        {campaigns}
        {tags}
        {onKeyUpHandler}
      />
    {/if}
  {/if}
</div>

<style lang="scss">
  .targeting-create-form {
    .section {
      p {
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }

      margin-bottom: var(--margin-large);
    }
  }
</style>
